/**
 * Pure utility functions for timezone conversions
 */

/**
 * Get current time in Eastern timezone formatted for datetime-local input
 */
export function getMinDateTime(): string {
    const now = new Date()
    const easternFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    })

    const parts = easternFormatter.formatToParts(now)
    const year = parts.find(p => p.type === 'year')!.value
    const month = parts.find(p => p.type === 'month')!.value
    const day = parts.find(p => p.type === 'day')!.value
    const hours = parts.find(p => p.type === 'hour')!.value
    const minutes = parts.find(p => p.type === 'minute')!.value

    return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * Convert datetime-local string (interpreted as Eastern time) to UTC ISO string
 */
export function convertEasternToUTC(dateTimeLocal: string): string {
    const [datePart, timePart] = dateTimeLocal.split('T')
    const [year, month, day] = datePart.split('-').map(Number)
    const [hours, minutes] = timePart.split(':').map(Number)

    let utcGuess = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0))

    const easternFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    })

    for (let i = 0; i < 10; i++) {
        const parts = easternFormatter.formatToParts(utcGuess)
        const easternMonth = parseInt(parts.find(p => p.type === 'month')!.value)
        const easternDay = parseInt(parts.find(p => p.type === 'day')!.value)
        const easternHour = parseInt(parts.find(p => p.type === 'hour')!.value)
        const easternMinute = parseInt(parts.find(p => p.type === 'minute')!.value)

        const monthDiff = month - easternMonth
        const dayDiff = day - easternDay
        const hourDiff = hours - easternHour
        const minuteDiff = minutes - easternMinute

        if (monthDiff === 0 && dayDiff === 0 && hourDiff === 0 && minuteDiff === 0) {
            break
        }

        const totalDiffMinutes = (monthDiff * 30 + dayDiff) * 24 * 60 + hourDiff * 60 + minuteDiff
        utcGuess = new Date(utcGuess.getTime() + totalDiffMinutes * 60 * 1000)
    }

    return utcGuess.toISOString()
}

/**
 * Convert UTC datetime string to Eastern timezone datetime-local format
 */
export function convertUTCToEasternDateTimeLocal(utcDateTime: string): string {
    if (!utcDateTime) return ''
    try {
        const date = new Date(utcDateTime)
        const year = date.toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric' })
        const month = String(date.toLocaleString('en-US', { timeZone: 'America/New_York', month: '2-digit' })).padStart(2, '0')
        const day = String(date.toLocaleString('en-US', { timeZone: 'America/New_York', day: '2-digit' })).padStart(2, '0')
        const hours = String(date.toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', hour12: false })).padStart(2, '0')
        const minutes = String(date.toLocaleString('en-US', { timeZone: 'America/New_York', minute: '2-digit' })).padStart(2, '0')
        return `${year}-${month}-${day}T${hours}:${minutes}`
    } catch {
        return ''
    }
}

/**
 * Format datetime string to Eastern timezone display format
 */
export function formatDateTime(dateTimeString: string): string {
    if (!dateTimeString) return ''
    try {
        const date = new Date(dateTimeString)
        return date.toLocaleString('en-US', {
            timeZone: 'America/New_York',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        })
    } catch {
        return dateTimeString
    }
}

